import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { callablePhone } from "@/lib/data/physician-phone";
import { whatsappHref } from "@/lib/labs/format";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Href `tel:` en E.164 marocain (+212...) — composable depuis l'étranger.
 *
 * Audit local SEO 2026-08-17 : les fiches médecin émettaient `tel:0522...`
 * (format national), non composable pour les MRE (France = 2e pays, ~1000
 * clics/28j) alors que les fiches pharmacien utilisaient déjà +212. Un
 * `tel:` national depuis un mobile FR appelle un numéro français invalide.
 * Numéros déjà internationaux (+212 / 00212) et non-marocains : conservés.
 */
export function telHrefMa(phone: string): string {
  const cleaned = phone.replace(/[^\d+]/g, "");
  if (cleaned.startsWith("+")) return `tel:${cleaned}`;
  if (cleaned.startsWith("00")) return `tel:+${cleaned.slice(2)}`;
  if (cleaned.startsWith("212")) return `tel:+${cleaned}`;
  if (cleaned.startsWith("0") && cleaned.length === 10) return `tel:+212${cleaned.slice(1)}`;
  return `tel:${cleaned}`;
}

export function formatPhoneMa(phone: string): string {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.startsWith("212")) {
    return `+${cleaned.slice(0, 3)} ${cleaned.slice(3, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 10)} ${cleaned.slice(10, 12)}`;
  }
  if (cleaned.startsWith("0")) {
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 10)}`;
  }
  return phone;
}

/**
 * D\u00e9code les entit\u00e9s HTML les plus courantes avant slugify, pour \u00e9viter que
 * `Sanaa N&#39;hammoucha` produise le slug `sanaa-n39hammoucha` (le `39`
 * \u00e9tant alphanum\u00e9rique, slugify le conserve). Cf. fix similaire dans
 * apps/web/src/lib/data/url-builder.ts:135 et packages/scraper/src/normalization/physician.ts.
 * Incident GSC 2026-06-02 : 498 URLs 404.
 */
function decodeHtmlEntities(s: string): string {
  return s
    .replace(/&#0*39;|&apos;/gi, "'")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#0*34;/gi, '"')
    .replace(/&nbsp;/gi, " ");
}

export function slugify(input: string): string {
  return decodeHtmlEntities(input)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

/**
 * Lien wa.me (message pré-rempli facultatif), ou null quand le numéro ne peut
 * pas ouvrir de conversation : jamais de lien cassé, donc jamais de bouton mort.
 *
 * wa.me n'accepte que le numéro international sans « + ». « wa.me/0661… »
 * (format national, celui de la quasi-totalité du catalogue) ouvrait une
 * erreur WhatsApp (relecture du 06/10/2026). Le tri est celui du bouton
 * « Appeler » (callablePhone), et le lien marocain celui des laboratoires
 * (whatsappHref) : une seule règle pour tout le site.
 *  - marocain, quelle que soit l'écriture (« 06 61 40 23 55 », « 06-61… »,
 *    « +212 6… », « 00212… », « 212… ») → wa.me/2126…, indicatif jamais doublé ;
 *  - étranger écrit avec son indicatif (« +33 6 86 87 22 46 ») → wa.me/33686872246 ;
 *  - factice (« 05 22 00 00 00 »), tronqué (« 522486948 »), indicatif doublé
 *    (« 212+212… »), étranger sans « + », vide → null.
 * Avant le tri : le « (0) » de « +212 (0)6… » ou « +33 (0)6… » (0 national,
 * à ne pas composer depuis l'étranger) est retiré, puis tout ce qui n'est ni
 * chiffre ni « + » (espaces, tirets, points, parenthèses).
 */
export function buildWhatsappLink(phone: string | null | undefined, message?: string): string | null {
  const compact = (phone ?? "").replace(/(\d)\s*\(0\)/g, "$1").replace(/[^\d+]/g, "");
  const callable = callablePhone(compact);
  if (callable === null) return null;
  const href = whatsappHref(callable) ?? `https://wa.me/${callable.replace(/\D/g, "")}`;
  return message ? `${href}?text=${encodeURIComponent(message)}` : href;
}
